import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { authContext, Company } from '@staffedupusa/shared-components';
import React, {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';

import { white } from '@styles/colors';
import UpdateEnterPrisePaymentPlanModal from '@features/payments/components/UpdateEnterPrisePaymentPlanModal/UpdateEnterPrisePaymentPlanModal';
import { PaymentPeriod, PaymentPlan } from '@features/payments';
import { useGetPaymentPlans } from '@features/payments/hooks/useGetPaymentPlans';
import UpdateCardInfo from '@features/enterprise/components/UpdateCardInfo';
import { useGetPaymantDetails } from '@features/payments/hooks/useGetPaymantDetails';
import { useGetEnterpriseCompaniesPlans } from '@features/payments/hooks/useGetEnterpriseCompaniesPlans';
import CompaniesListWithPayment from '@features/payments/components/PaymentCardDetail/CompaniesListWithPayment';
import CompaniesListWithPaymentSekelton from '@features/payments/components/PaymentCardDetail/CompaniesListWithPaymentSekelton';
import { ChangeCircle } from '@mui/icons-material';
import ConfirmationUpdateBillingModal from '@features/payments/components/PaymentCardDetail/ConfirmationUpdateBillingModal';
import { useGetTotalAggregate } from '@features/payments/hooks/useGetTotalAggregate';
import BulkSwitchPlanModal from '@features/payments/components/PaymentCardDetail/BulkSwitchPlanModal';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

type ExtendedCompany = Company & {
  is_company_paid: boolean;
  sub_users?: { length: number }[];
  plan: 0 | 1 | 2 | 999;
  subscription?: {
    date_created: string;
    date_updated: string;
    id: number;
    stripe_id: string;
    billing_cycle?: string;
    next_billing_date?: string;
    total_amount?: number;
  };
};

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const CompaniesTable = ({
  isLoading,
  companies,
  filterCondition,
  setSelectedCompanies,
  selectedCompanies,
  totalAggregate,
}: {
  isLoading: boolean;
  totalAggregate: any;
  setSelectedCompanies: Dispatch<SetStateAction<number[]>>;
  selectedCompanies: number[];
  companies: ExtendedCompany[] | undefined;
  filterCondition: (company: ExtendedCompany) => boolean;
}) => {
  const planOrder = { 2: 1, 1: 2, 999: 3, 0: 4 };
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked && companies) {
      const filteredIds = companies
        .filter((c) => filterCondition(c))
        .map((c) => c.id);

      setSelectedCompanies(filteredIds);
    } else {
      setSelectedCompanies([]);
    }
  };

  const handleSelectCompany = (companyId: number) => {
    setSelectedCompanies((prevSelected) =>
      prevSelected.includes(companyId)
        ? prevSelected.filter((id) => id !== companyId)
        : [...prevSelected, companyId],
    );
  };

  if (isLoading) {
    return <CompaniesListWithPaymentSekelton />;
  }

  if (!companies || companies.length === 0) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="300px"
        textAlign="center"
        bgcolor={white}
        width="100%"
        borderRadius={8}
      >
        <Typography variant="body1" color="textSecondary">
          No companies available.
        </Typography>
      </Box>
    );
  }

  const filteredIds = companies
    .filter((c) => filterCondition(c))
    .map((c) => c.id);
  const allSelected =
    selectedCompanies.length > 0 &&
    selectedCompanies.length === filteredIds.length;
  const isIndeterminate =
    selectedCompanies.length > 0 &&
    selectedCompanies.length < filteredIds.length;

  return (
    <>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Stack direction="row" alignItems="center">
                  <Checkbox
                    indeterminate={isIndeterminate}
                    disabled={filteredIds.length === 0} // Disable if no valid companies
                    checked={allSelected}
                    onChange={handleSelectAll}
                  />
                  {selectedCompanies?.length > 0 && (
                    <Typography>{selectedCompanies?.length}</Typography>
                  )}
                </Stack>
              </TableCell>
              <TableCell>
                <b>Company</b>
              </TableCell>
              <TableCell>
                <b>Plan</b>
              </TableCell>
              <TableCell>
                <b>Total</b>
              </TableCell>
              <TableCell>
                <b>Status</b>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {companies.filter(filterCondition)?.length > 0 ? (
              companies
                .filter(filterCondition)
                .sort(
                  (a, b) => (planOrder[a.plan] || 5) - (planOrder[b.plan] || 5),
                )
                .map((company) => (
                  <CompaniesListWithPayment
                    company={company}
                    key={company.id}
                    selectedCompanies={selectedCompanies}
                    handleSelectCompany={handleSelectCompany}
                    totalAggregate={
                      totalAggregate
                        ? totalAggregate?.find(
                          (t: { company_id: number }) =>
                            t.company_id === company.id,
                        )
                        : null
                    }
                  />
                ))
            ) : (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    height="200px"
                    textAlign="center"
                    bgcolor={white}
                    width="100%"
                    borderRadius={8}
                  >
                    <Typography variant="body1" color="textSecondary">
                      No companies available
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

interface ManageBillingTabProps {
  totalAggregate: any;
}

const ManageBillingTab: React.FC<ManageBillingTabProps> = ({
  totalAggregate,
}) => {
  const { authData } = useContext(authContext);
  const { totalAggregate: check, isLoadingTotalAggregate } =
    useGetTotalAggregate();
  const [value, setValue] = React.useState(0);
  const [isUpdatePlanModalOpen, setIsUpdatePlanModalOpen] = useState(false);
  const [selectedCompanies, setSelectedCompanies] = useState<number[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState('');
  const [showBulkSwitchPlan, setShowBulkSwitchPlan] = useState(false);
  const [hasPaymentMethod, setHasPaymentMethod] = useState(false);
  const [errorText, setErrorText] = useState<string>('');
  const { paymentInfo } = useGetPaymantDetails();
  const { companiesPlansData, isLoadingCompaniesData } =
    useGetEnterpriseCompaniesPlans({ setErrorText });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedCompanies([]);
    setValue(newValue);
  };

  const hanldeSuccess = () => {
    setSelectedCompanies([]);
    setSelectedPlanId(null);
    setShowConfirmationModal('');
  };

  useEffect(() => {
    if (
      paymentInfo &&
      paymentInfo?.object &&
      paymentInfo?.object?.data?.length > 0 &&
      authData?.user?.billing_cycle && authData.user.billing_cycle > 0
    ) {
      setHasPaymentMethod(true);
    }
  }, [paymentInfo]);

  return (
    <Stack
      spacing={3}
      bgcolor={white}
      p={3}
      borderRadius="8px"
      width="100%"
      alignItems="center"
    >
      <Box sx={{ width: '100%' }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexDirection: { xs: 'column', md: 'row' },
            gap: '5px',
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            aria-label="basic tabs example"
          >
            <Tab label="Enterprise Billing" {...a11yProps(0)} />
            <Tab label="Location Billing" {...a11yProps(1)} />
          </Tabs>
          <Stack direction={{xs:'column', md:'row'}} spacing={2}>
            {value === 0 && (
          <Tooltip
            placement="top"
            arrow
            title={
              selectedCompanies.length === 0
                ? 'Select Companies to change Plan'
                : ''
            }
          >
            <div style={{ flexShrink: 0 }}>
              <Button
                variant="contained"
                color="secondary"
                disabled={selectedCompanies.length === 0}
                startIcon={<ChangeCircle />}
                onClick={() =>
                  setShowBulkSwitchPlan(true)
                }
              >
                Change Plan
              </Button>
            </div>
          </Tooltip>
            )}
          <Tooltip
            placement="top"
            arrow
            title={
              selectedCompanies.length === 0
                ? 'Select Companies to change Billing'
                : ''
            }
          >
            <div style={{ flexShrink: 0 }}>
              <Button
                variant="contained"
                color="primary"
                disabled={selectedCompanies.length === 0}
                startIcon={<ChangeCircle />}
                onClick={() =>
                  setShowConfirmationModal(
                    value === 0 ? 'self paid' : 'enterprise paid',
                  )
                }
              >
                Assign Billing to {value === 0 ? 'Location' : 'Enterprise'}
              </Button>
            </div>
          </Tooltip>
          </Stack>
        </Box>
        <CustomTabPanel value={value} index={0}>
          <CompaniesTable
            isLoading={isLoadingCompaniesData}
            companies={companiesPlansData?.objects}
            filterCondition={(company) => !company.is_company_paid}
            selectedCompanies={selectedCompanies}
            setSelectedCompanies={setSelectedCompanies}
            totalAggregate={totalAggregate}
          />
        </CustomTabPanel>
        <CustomTabPanel value={value} index={1}>
          <CompaniesTable
            isLoading={isLoadingCompaniesData}
            companies={companiesPlansData?.objects}
            filterCondition={(company) => company.is_company_paid}
            selectedCompanies={selectedCompanies}
            setSelectedCompanies={setSelectedCompanies}
            totalAggregate={totalAggregate}
          />
        </CustomTabPanel>
      </Box>

      <UpdateEnterPrisePaymentPlanModal
        isOpen={isUpdatePlanModalOpen}
        handleClose={() => setIsUpdatePlanModalOpen(false)}
      />
      {selectedPlanId && (
        <UpdateCardInfo
          selectedPlanId={selectedPlanId}
          handleClose={() => setSelectedPlanId(null)}
        />
      )}
      {showConfirmationModal ? (
        <ConfirmationUpdateBillingModal
          isOpen={showConfirmationModal}
          handleClose={() => {
            setSelectedCompanies([]);
            setShowConfirmationModal('')
          }}
          handleSuccess={hanldeSuccess}
          companyIds={selectedCompanies}
          companyName={selectedCompanies?.length === 1 ? companiesPlansData?.objects?.find(
            (company: ExtendedCompany) => selectedCompanies.includes(company.id),
          )?.s_name : undefined}
          planIds={companiesPlansData?.objects
            .filter((company: ExtendedCompany) =>
              selectedCompanies.includes(company.id),
            )
            .map((company: ExtendedCompany) => ({
              company_id: company.id,
              plan_id: company.plan,
              new_plan_id: company.plan,
            }))}
          expiredCompanyName={companiesPlansData?.objects
            .filter((company: ExtendedCompany) =>
              selectedCompanies.includes(company.id) && ((company.plan === 999) || (company.plan === 0)),
            )
            .map((company: ExtendedCompany) => ({
              companyName: company.s_name,
              companyId: company.id,
            }))}
          hasPaymentMethod={hasPaymentMethod}
        />
      ) : null}
      {showBulkSwitchPlan && (
        <BulkSwitchPlanModal
          isOpen={showBulkSwitchPlan}
          handleClose={() => {
            setShowBulkSwitchPlan(false)
            setSelectedCompanies([]);
          }}
          planIds={companiesPlansData?.objects
            .filter((company: ExtendedCompany) =>
              selectedCompanies.includes(company.id),
            )
            .map((company: ExtendedCompany) => ({
              company_id: company.id,
              plan_id: company.plan,
              new_plan_id: company.plan,
            }))}
          hasPaymentMethod={hasPaymentMethod}
        />
      )}
    </Stack>
  );
};

export default ManageBillingTab;